import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import API from "../../../service/api";
import ImageFallback from 'react-image-fallback';
import { AdminPage } from "../AdminPage";
import { DataTable, DataTableColumnDef, DataTableDateComparison, DataTableStringComparison } from "../../../components/DataTable";
import { MachineEdit } from "./MachineEdit";
import { ImportMachineModal } from "./ImportMachineModal";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { SearchInput } from "../../../components/SearchInput";
import { capitalizeFirst, simpleDateFormat } from "../../../service/utils";
import { utils, writeFile } from "xlsx";

export interface MachinesPageProps {
    canEdit: boolean;
    canAssignToLocation: boolean;
    canTransfer: boolean;
    canActivate: boolean;
}
export const MachinesPage = ({canEdit, canAssignToLocation, canTransfer, canActivate}: MachinesPageProps) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [importMachine, setImportMachine] = useState(false);
    const [data, setData] = useState<any[]|null>(null);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    
    const qmid = useMemo(() => searchParams.get('mid'), [searchParams]);
    
    const fetchMachines = useCallback(() => {
        API.GET('/api/admmachines')
            .then(data => {
                setData(data);
                if (qmid && data.find((m: any) => m.id === Number(qmid))) setSelectedId(Number(qmid));
            })
            .catch(err => {
                setError(err.message);
                setData([]);
            });
    }, [qmid]);

    useEffect(() => {
        fetchMachines();
    }, []);

    const TableColumns = useMemo<DataTableColumnDef<any>[]>(() => ([
        {
            headerName: t('name'),
            valueGetter: ({data}) => ({name: data.name, type: data.type, sn: data.sn}),
            flex: 4,
            cellRenderer: ({value}) => (
                <div className="d-flex flex-row align-items-center w-100 h-100">
                    <span><ImageFallback src={`/mtypes/mt${value.type?.id}.png`} width={22} height={22} className="me-1" title={value.type?.name}  fallbackImage={<span></span>}/></span>
                    <div className="d-flex flex-column" style={{lineHeight: '1.1rem'}}>
                        <span>{value.name}</span>
                        <span className="text-muted" style={{fontSize: '80%'}}>{value.sn}</span>
                    </div>
                </div>
            ),
            quickFilterText: data => `${data.type}${data.name}${data.sn}`,
            comparator: (row1, row2) => DataTableStringComparison(row1.data.name, row2.data.name)
        },
        {
            headerName: t('location'),
            valueGetter: ({data}) => data.location?.Name,
            cellRenderer: ({value}) => (
                value ?
                <span className="d-flex flex-row align-items-center"><i className="fas fa-map-marker-alt text-primary me-1"/>{value}</span> :
                <span className="fst-italic">{t('unassigned')}</span>
            ),
            flex: 2
        },
        {headerName: t('boxes'), field: 'nrOfBoxes', flex: 1},
        {
            headerName: t('subscription_status'),
            valueGetter: ({data}) => {
                let inactiveUntil: Date|null = null;
                if (data.deliverAt) {
                    inactiveUntil = new Date(data.deliverAt);
                    inactiveUntil.setFullYear(inactiveUntil.getFullYear() + 1);
                }
                return {
                    expireAt: data.expireAt ? new Date(data.expireAt) : null,
                    inactiveUntil
                }
            },
            cellRenderer: ({value}) => (
                <div className="d-flex flex-row align-items-center w-100 h-100">
                    {value.expireAt == null && (
                        <span className="badge badge-dark">{t('inactive')}: {t('until')} {(value.inactiveUntil as Date)?.toLocaleDateString() ?? "??"}</span>
                    )}
                    {value.expireAt && (value.expireAt as Date).getTime() < new Date().getTime() && (
                        <span className="badge badge-danger">{capitalizeFirst(t('expired'))}: {(value.expireAt as Date).toLocaleDateString()}</span>
                    )}
                    {value.expireAt && (value.expireAt as Date).getTime() >= new Date().getTime() && (
                        // <span>{(value as Date).toLocaleString()}</span>
                        <span className="badge badge-success">{t('ok')}: {(value.expireAt as Date).toLocaleDateString()}</span>
                    )}
                </div>
            ),
            comparator: (row1, row2) => DataTableDateComparison(
                row1.data.expireAt ?? row1.data.inactiveUntil ?? new Date(1990, 1, 1),
                row2.data.expireAt ?? row2.data.inactiveUntil ?? new Date(1990, 1, 1)
            )
        }
    ]), []);

    const Table = useMemo(() => (
        <DataTable<any>
            data={data}
            search={search}
            height={500}
            onRowSelected={m => setSelectedId(m.id)}
            columns={TableColumns}
        />
    ), [TableColumns, data, search]);

    const MachineEditComponent = useMemo(() => selectedId == null ? null : (
        <MachineEdit 
            machineId={selectedId}
            canEdit={canEdit}
            canTransfer={canTransfer}
            canAssignToLocation={canAssignToLocation}
            canActivate={canActivate}
            onEdit={() => fetchMachines()}
            onClose={() => setSelectedId(null)}
        />
    ), [selectedId, canEdit, canTransfer, canAssignToLocation, canActivate, fetchMachines]);

    return (
        <AdminPage
            title={t('machines')}
            buttons={[
                {title: t('import_machine'), icon: 'right-to-bracket', onClick: () => setImportMachine(true)},
                {title: t('export_to_excel'), icon: 'file-excel', onClick: () => {
                    if (data) exportToFile(data, "excel")
                }},
                {title: t('export_to_csv'), icon: 'file-csv', onClick: () => {
                    if (data) exportToFile(data, "csv")
                }}
            ]}
            error={error}
        >
            <ImportMachineModal
                open={importMachine}
                onClose={() => setImportMachine(false)}
                onJoin={id => { fetchMachines(); setSelectedId(id); }}
            />
            <SearchInput onSearch={setSearch}/>
            <SplitScreenDiv
                ratio={55}
                left={Table}
                right={MachineEditComponent}
            />
        </AdminPage>
    )
}

const exportToFile = (machines: any[], format: "csv"|"excel") => {
    const crtDate = new Date();

    const filename = `machines_${simpleDateFormat(crtDate)}`;
    const columns = ['Id', 'SN', 'Name', 'Expire at'];

    const rows = machines.map(m => {
        return [m.id, m.sn, m.name, m.expireAt ? simpleDateFormat(new Date(m.expireAt)) : '?'];
    });

    const ws = utils.aoa_to_sheet([
        columns,
        ...rows
    ]);
    const wb = utils.book_new();

    utils.book_append_sheet(wb, ws, "Sheet1");

    if (format == 'excel'){
        writeFile(wb, `${filename}.xlsx`);
    }else if (format == 'csv'){
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/plain' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }
}